import { useEffect, useMemo, useRef, useState } from 'react';
import { SelectInput } from 'stubben-edge-unified-frontend-react-components';
import type { SelectOptionProp } from '@ui/SelectField/types';
import type { DataTableProviderFilterItem } from './types';

type DatatableFilterSelectInputProps = {
  filter: DataTableProviderFilterItem;
  label: string;
  value: string | number | '';
  disabled?: boolean;
  testIdentifier: string;
  onChange: (
    value: string | number | string[],
    option?: SelectOptionProp
  ) => void;
};

function parseSelectedValues(value: string | number | ''): Array<string | number> {
  if (value === '' || value == null) return [];
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const asNumber = Number(part);
      return Number.isNaN(asNumber) ? part : asNumber;
    });
}

export function DatatableFilterSelectInput({
  filter,
  label,
  value,
  disabled = false,
  testIdentifier,
  onChange
}: DatatableFilterSelectInputProps) {
  const isMulti = Boolean(filter.multiSelect);
  const [options, setOptions] = useState<SelectOptionProp[]>(
    filter.selectOptions ?? []
  );
  const [loading, setLoading] = useState(!!filter.selectOptionsResolver);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (filter.selectOptions) {
      setOptions(filter.selectOptions);
      setLoading(false);
      return;
    }

    if (!filter.selectOptionsResolver) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    filter
      .selectOptionsResolver()
      .then((data) => {
        if (!cancelled) {
          setOptions(data ?? []);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filter.selectOptions, filter.selectOptionsResolver]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const selectedOptions = useMemo(
    () => parseSelectedValues(value),
    [value]
  );

  const selectedLabel = useMemo(() => {
    if (value === '' || value == null) return '';
    const match = options.find(
      (option) => String(option.value) === String(value)
    );
    return match?.label ?? String(value);
  }, [options, value]);

  const [searchText, setSearchText] = useState(selectedLabel);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) {
      setSearchText(selectedLabel);
    }
  }, [selectedLabel]);

  const handleSearchTextChange = (text: string) => {
    isEditingRef.current = true;
    setSearchText(text);
    if (!filter.searchSelectOptions) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setLoading(true);
      filter
        .searchSelectOptions?.(text)
        .then((data) => {
          if (data) setOptions(data);
        })
        .finally(() => setLoading(false));
    }, 250);
  };

  if (isMulti) {
    return (
      <SelectInput
        multi
        label={label}
        placeholder={filter.placeholder || filter.label}
        options={options}
        loading={loading}
        disabled={disabled || (loading && options.length === 0)}
        selectedOptions={selectedOptions}
        enableClearButton
        handleTextInputOnChange={
          filter.searchSelectOptions ? handleSearchTextChange : undefined
        }
        onSelectOption={(selectedValue) => {
          const option = options.find(
            (o) => `${o.value}` === `${selectedValue}`
          );
          onChange(selectedValue as string | number, option);
        }}
        onRemoveOption={(removedValue) => {
          const option = options.find((o) => `${o.value}` === `${removedValue}`);
          onChange(removedValue as string | number, option);
        }}
        onClearOptions={() => onChange('', undefined)}
        testIdentifier={testIdentifier}
      />
    );
  }

  return (
    <SelectInput
      label={label}
      placeholder={filter.placeholder || filter.label}
      options={options}
      loading={loading}
      disabled={disabled || (loading && options.length === 0)}
      value={searchText}
      onChange={handleSearchTextChange}
      selectedOption={value === '' ? undefined : value}
      handleTextInputOnChange={
        filter.searchSelectOptions ? handleSearchTextChange : undefined
      }
      onSelectOption={(selectedValue) => {
        const option = options.find((o) => `${o.value}` === `${selectedValue}`);
        isEditingRef.current = false;
        onChange(selectedValue as string | number, option);
        setSearchText(option?.label ?? String(selectedValue));
      }}
      onRemoveOption={() => {
        isEditingRef.current = false;
        onChange('', undefined);
        setSearchText('');
      }}
      testIdentifier={testIdentifier}
    />
  );
}
